import React, { useMemo, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { FactSection, SourceNote } from './CollegeParts';
import { formatCalendarDate } from '../../utils/calendarDate';
import { track, EVENTS } from '../../services/analytics';

// Every major a college offers, grouped by its own schools (founder,
// 2026-09-30: "a good one stop show"). Groups are <details>: collapsed by
// default when the list is long, but every name stays in the HTML, so the
// prerendered page, crawlers and no-JS readers get the whole list. The
// filter only narrows what a JavaScript reader sees.
const COLLAPSE_OVER = 40;

const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

function GroupSource({ source, read }) {
    return (
        <p className="text-xs text-[#6B6B6B]">
            Source:{' '}
            <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track(EVENTS.SOURCE_LINK_CLICKED, { source: 'college_majors', surface: 'college-page' })}
                className="font-medium text-[#1A4D2E] underline underline-offset-2"
            >
                {source.label}
                <span className="sr-only"> (opens in a new tab)</span>
            </a>
            . Read {formatCalendarDate(read)}.
        </p>
    );
}

export default function MajorsSection({ college }) {
    const { majors, sources, shortName, slug } = college;
    const [q, setQ] = useState('');
    const total = majors.groups.reduce((n, g) => n + g.majors.length, 0);
    const collapse = total > COLLAPSE_OVER;
    const query = norm(q);
    const shown = useMemo(() => majors.groups.map((g) => ({
        ...g,
        hits: query ? g.majors.filter((m) => norm(m.name).includes(query)) : g.majors,
    })), [majors, query]);
    const matches = shown.reduce((n, g) => n + g.hits.length, 0);

    return (
        <FactSection id="majors" title="Majors">
            <p>
                <strong className="text-[#1A2E1F]">{total} majors</strong> across {majors.groups.length}{' '}
                {majors.groups.length === 1 ? 'school' : 'schools'}. {majors.intro}
            </p>
            <label className="block">
                <span className="sr-only">Filter {shortName} majors</span>
                <span className="relative block">
                    <MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B6B6B]" />
                    <input
                        type="search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onBlur={() => { if (q.trim()) track(EVENTS.COLLEGE_MAJORS_FILTERED, { slug, matches }); }}
                        placeholder={`Find a major at ${shortName}`}
                        className="min-h-[44px] w-full rounded-xl border border-[#E0DED8] bg-white pl-9 pr-3 text-[15px] text-[#1A2E1F] focus:border-[#1A4D2E] focus:outline-none"
                    />
                </span>
            </label>
            {query && (
                <p aria-live="polite" className="text-sm text-[#4A4A4A]">
                    {matches === 0 ? `No ${shortName} major matches "${q.trim()}".` : `${matches} ${matches === 1 ? 'major matches' : 'majors match'}.`}
                </p>
            )}
            <div className="space-y-2">
                {shown.map((g) => (
                    <details
                        key={g.name}
                        open={!collapse || !!query}
                        hidden={!!query && g.hits.length === 0}
                        className="group rounded-xl border border-[#E0DED8] bg-white"
                    >
                        <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-3 px-4 py-2">
                            <span className="font-semibold text-[#1A2E1F]">{g.name}</span>
                            <span className="shrink-0 text-sm text-[#6B6B6B]">
                                {query ? `${g.hits.length} of ${g.majors.length}` : g.majors.length}
                                <span aria-hidden="true" className="ml-2 inline-block transition-transform group-open:rotate-90">&rsaquo;</span>
                            </span>
                        </summary>
                        <div className="space-y-2 border-t border-[#EFEDE7] px-4 py-3">
                            {g.rate && (
                                <p className="text-sm text-[#1A2E1F]">{g.rate.text}</p>
                            )}
                            {g.rate && <SourceNote sources={sources} sourceKeyName={g.rate.source} />}
                            {g.note && <p className="text-sm text-[#4A4A4A]">{g.note}</p>}
                            {g.note && g.noteSource && <SourceNote sources={sources} sourceKeyName={g.noteSource} />}
                            <ul className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
                                {g.majors.map((m) => (
                                    <li
                                        key={`${m.name}-${m.degree}`}
                                        hidden={!!query && !norm(m.name).includes(query)}
                                        className="text-[15px] leading-snug"
                                    >
                                        <span className="text-[#1A2E1F]">{m.name}</span>
                                        {m.degree && <span className="text-[#6B6B6B]"> ({m.degree})</span>}
                                        {m.note && <span className="block text-xs text-[#6B6B6B]">{m.note}</span>}
                                        {m.flag && (
                                            <span className="mt-0.5 block text-xs text-[#8A4B2A]">
                                                <span className="font-semibold">Extra requirement:</span> {m.flag}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            <GroupSource source={g.source} read={majors.read} />
                        </div>
                    </details>
                ))}
            </div>
            {[...new Set(majors.groups.flatMap((g) => g.majors).map((m) => m.flagSource).filter(Boolean))].map((key) => (
                <SourceNote key={key} sources={sources} sourceKeyName={key} where="for the extra requirements" />
            ))}
        </FactSection>
    );
}
